import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { FileText, Loader2, Eye, Pencil, Maximize2, X } from "lucide-react";
import { buildStyledHtml } from "../../utils/buildStyledHtml";
import { buildDocxBlob } from "../../utils/buildDocx";
import { generatePdfFromHtml } from "../../service/apiService";

interface DocumentExportProps {
  text: string;
  fileName?: string;
}

const DocumentExport: React.FC<DocumentExportProps> = ({ text, fileName = "document" }) => {
  const [markdown, setMarkdown] = useState<string>(text);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [mode, setMode] = useState<"preview" | "edit">("preview");
  const [isOpen, setIsOpen] = useState(false);

  // Ref for inline iframe to update content in-place (no reload/blink)
  const inlineIframeRef = useRef<HTMLIFrameElement | null>(null);
  const iframeInitializedRef = useRef(false);

  useEffect(() => {
    setMarkdown(text);
  }, [text]);

  const previewHtml = useMemo(() => buildStyledHtml(markdown), [markdown]);

  // Update inline iframe body in-place to avoid full reload blinks
  useEffect(() => {
    const iframe = inlineIframeRef.current;
    if (!iframe) return;

    const doc = iframe.contentDocument;
    if (!doc) return;

    if (!iframeInitializedRef.current) {
      // First time: write the full HTML to initialize styles, head, etc.
      doc.open();
      doc.write(previewHtml);
      doc.close();
      iframeInitializedRef.current = true;
    } else {
      // Subsequent updates: only replace the body content to avoid reload
      const newDoc = new DOMParser().parseFromString(previewHtml, 'text/html');
      doc.body.innerHTML = newDoc.body.innerHTML;
    }
  }, [previewHtml]);

  // Handle iframe ref callback to write initial content
  const setInlineIframeRef = useCallback((el: HTMLIFrameElement | null) => {
    inlineIframeRef.current = el;
    if (el) {
      // Write initial content once the iframe element is mounted
      const doc = el.contentDocument;
      if (doc) {
        doc.open();
        doc.write(previewHtml);
        doc.close();
        iframeInitializedRef.current = true;
      }
    } else {
      iframeInitializedRef.current = false;
    }
  }, []); // intentionally empty — we only need this for mount

  const downloadDocx = async () => {
    const blob = await buildDocxBlob(markdown);
    triggerDownload(blob, `${fileName}.docx`);
  };

  const downloadPdf = async () => {
    setIsPdfLoading(true);
    try {
      const styledHtml = buildStyledHtml(markdown);
      const pdfBlob = await generatePdfFromHtml(styledHtml, fileName);
      triggerDownload(pdfBlob, `${fileName}.pdf`);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    } finally {
      setIsPdfLoading(false);
    }
  };

  function triggerDownload(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // ─── Inline card (unchanged layout, just an open trigger) ─────
  return (
    <>
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-2 bg-gray-50/80 border-b border-gray-200">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={15} className="text-indigo-500 shrink-0" />
            <span className="text-sm font-semibold text-gray-700 truncate">
              {fileName}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={downloadDocx}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Export Word
            </button>
            <button
              onClick={downloadPdf}
              disabled={isPdfLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors"
            >
              {isPdfLoading && <Loader2 size={13} className="animate-spin" />}
              Export PDF
            </button>
            <button
              onClick={() => setIsOpen(true)}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Full view"
            >
              <Maximize2 size={14} />
            </button>
          </div>
        </div>

        {/* Inline preview — uses ref-based update instead of srcDoc to avoid blinks */}
        <div style={{ height: "520px" }}>
          <iframe
            ref={setInlineIframeRef}
            title="Document preview"
            className="w-full h-full border-0 bg-white"
            sandbox="allow-same-origin"
            style={{ display: "block" }}
          />
        </div>
      </div>

      {/* ── Full-view modal (same pattern as SettingsDialog) ───── */}
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 scrim" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative bg-white rounded-2xl text-left align-middle shadow-xl transition-all w-full lg:w-[1100px] sm:w-[90vw] h-[80vh] sm:h-[80vh] lg:h-[650px] overflow-hidden flex flex-col">
                  {/* Header */}
                  <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText size={18} className="text-indigo-500 shrink-0" />
                      <h2 className="text-lg font-semibold text-gray-800 truncate">{fileName}</h2>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Mode toggle */}
                      <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                        <button
                          onClick={() => setMode("preview")}
                          className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            mode === "preview"
                              ? "bg-white text-gray-800 shadow-sm"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                        >
                          <Eye size={13} />
                          Preview
                        </button>
                        <button
                          onClick={() => setMode("edit")}
                          className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                            mode === "edit"
                              ? "bg-white text-gray-800 shadow-sm"
                              : "text-gray-500 hover:text-gray-700"
                          }`}
                        >
                          <Pencil size={13} />
                          Edit
                        </button>
                      </div>
                      {/* Export buttons */}
                      <button
                        onClick={downloadDocx}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        Export Word
                      </button>
                      <button
                        onClick={downloadPdf}
                        disabled={isPdfLoading}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition-colors"
                      >
                        {isPdfLoading && <Loader2 size={13} className="animate-spin" />}
                        Export PDF
                      </button>
                      {/* Close */}
                      <button
                        onClick={() => setIsOpen(false)}
                        className="rounded-full bg-transparent hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 p-2"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 overflow-hidden">
                    {mode === "preview" ? (
                      <iframe
                        srcDoc={previewHtml}
                        title="Document preview"
                        className="w-full h-full border-0 bg-white"
                        sandbox="allow-same-origin"
                        style={{ display: "block" }}
                      />
                    ) : (
                      <textarea
                        value={markdown}
                        onChange={(e) => setMarkdown(e.target.value)}
                        className="w-full h-full p-4 font-mono text-[13px] leading-relaxed text-gray-800 bg-white resize-none focus:outline-none"
                        spellCheck={false}
                        placeholder="Write your markdown here..."
                      />
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default DocumentExport;
